import { EventEmitter, Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { AppStateService } from 'core/app-state/app-state.service';
import { AppState } from 'core/common-entities/app-state.class';
import { NotificationType } from 'core/notification/notification-config.class';
import { HttpClient } from '@angular/common/http';
import { ImportItemsQuoteService } from 'src/app/modules/shared/import-quotes-and-orders/import-items-quote.service';
import { environment } from '../../../environments/environment';
import { NotificationService } from '../notification/notification.service';
import { AuthenticationToken } from './authentication-token.class';

declare global {
  interface Window {
    ipcRenderer: any;
  }
}

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {
  private stateKey = 'iscState';
  private cookieKey = 'iscAuthToken';
  private authPath = '/connect/authorize';
  private logoutPath = '/connect/endsession';
  private clientId = 'Impact.js';
  private responseType = 'code';
  private scope = 'ImpactApi B2BApi openid';
  private currentToken: AuthenticationToken;
  private callbackUrlLocal = 'http://localhost/callback/';
  private state: AppState;
  private tokenExchangeEndpoint = '/api/identity/token/getByCode';
  private offlineTokenKey = 'offlineToken';
  authenticatedOffline = false;
  isAuthenticating = false;
  apiRoot = '';

  offlineAuthenticationDone$ = new EventEmitter<boolean>();

  constructor(
    private cookieService: CookieService,
    private notificationService: NotificationService,
    private appStateService: AppStateService,
    private injector: Injector,
    private http: HttpClient,
    private importItemsQuoteService: ImportItemsQuoteService
  ) {
    this.state = this.appStateService.state;
    this.initializeApiRoot(this.appStateService.state.isElectron);

    this.appStateService.offlineStateToggle$.subscribe(isOnline => {
      this.state = this.appStateService.state;
      const shouldSync = window.localStorage.getItem(this.appStateService.shouldSyncKey);
      if (isOnline && shouldSync) {
        this.authorizeAndLogin();
      }
    });

    // if Electron, subscribe to SilentRenew event and call the iframe method for token renewal
    if (this.appStateService.state.isElectron && typeof window.ipcRenderer?.on === 'function') {
      window.ipcRenderer.on('SilentRenewUrl', (event, message) => this.loadSilentRenewFrame(message));
    }
  }

  /**
   * Checks if the user is authenticated
   */
  get isAuthenticated(): boolean {
    return this.state.isOnline && !!this.existingToken || this.authenticatedOffline;
  }

  /**
   * Retrieves the existing token from cookie
   */
  get existingToken(): AuthenticationToken {
    const existingToken = this.retrieveToken();

    if (existingToken) {
      try {
        const parsedToken = JSON.parse(existingToken);
        if (parsedToken) {
          parsedToken.expires = new Date(parsedToken.expires);
          this.currentToken = parsedToken;
          return parsedToken;
        }
      } catch (e) { }
    }

    return undefined;
  }

  /**
   * Returns the token
   * @param onDemand - if true, returns the token, no matter what online state is
   */
  getToken(): string {
    const token = this.existingToken;
    if (token) {
      return `${token.tokenType} ${token.accessToken}`;
    } else {
      return null;
    }
  }

  /**
   * Authorizes user to the server
   */
  async authorize(): Promise<boolean> {
    this.isAuthenticating = true;
    await this.processToken();
    this.isAuthenticating = false;

    if (this.isAuthenticated) {
      return true;
    }

    this.authorizeAndLogin();
    return false;
  }

  /**
   * Calls the authorization host for token renewal
   */
  async renewToken() {
    const url = await this.createAuthRequest(
      this.currentToken.idToken,
      `${this.state.isElectron ? '' : '/'}silentRenew.html`
    );
    const silentRenewFrame = document.getElementById('silentRenew');
    silentRenewFrame.setAttribute('src', url);
  }

  /**
   * Calls the authorization host for logout
   */
  logout() {
    if (this.state.isOnline) {
      const url = this.createLogoutRequestAndRemoveCookie();
      this.updateWindowLocationUrl(url);
    }
    this.importItemsQuoteService.removeDestinationQuoteId();
    window.localStorage.removeItem('selectedCountry');
  }

  /**
   * Expires the session and logs out
   */
  expireSession() {
    if (this.state.isOnline) {
      const url = this.createLogoutRequestAndRemoveCookie(/* sessionEnded */ true);
      this.updateWindowLocationUrl(url);
      this.importItemsQuoteService.removeDestinationQuoteId();
      window.localStorage.removeItem('selectedCountry');
    }
  }

  /**
   * Marks the user as authenticated offline
   */
  authenticateOffline(offlineToken: string) {
    if (!this.state.isOnline) {
      const token = {
        accessToken: offlineToken,
        tokenType: 'Bearer',
      };
      window.localStorage.setItem(this.offlineTokenKey, JSON.stringify(token));
      this.authenticatedOffline = true;
      this.offlineAuthenticationDone$.emit(true);
    }
  }

  logoutOffline() {
    if (!this.state.isOnline) {
      this.authenticatedOffline = false;
      window.localStorage.removeItem(this.offlineTokenKey);
    }
  }

  /**
   * Creates the logout request URL and removes the cookie
   * @param sessionEnded - True if session ended
   */
  private createLogoutRequestAndRemoveCookie(sessionEnded?: boolean) {
    const authorizationUrl = environment.authRoot + this.logoutPath;
    let redirectUri = this.state.isElectron ? this.callbackUrlLocal : `${environment.webRoot}`;
    redirectUri =
      encodeURI(redirectUri) +
      (sessionEnded ? `${this.appStateService.state.isElectron ? '' : '/'}sessionEnded.html` : '');

    const url = `${authorizationUrl
    }?id_token_hint=${this.currentToken.idToken
    }&post_logout_redirect_uri=${redirectUri}`;

    // clear token and redirect to logout endpoint
    this.storeToken('');
    sessionStorage.clear();
    return url;
  }

  /**
   * Authorizes user and logs in (if necessary)
   */
  private async authorizeAndLogin() {
    // remove the redirect to IndSSO
    if (this.appStateService.state.isInternetConnected) {
      if (this.state.isOnline) {
        const url = await this.createAuthRequest();
        setTimeout(() => this.updateWindowLocationUrl(url), 500);
      }
    } else {
      const router = this.injector.get(Router);
      router.navigate(['/no-internet']);
    }
  }

  /**
   * Generates a new authentication request
   * @param idTokenHint - The id token hint for token renewal
   * @param redirectPage - The page to be redirected to
   */
  private async createAuthRequest(idTokenHint?: string, redirectPage?: string) {
    const authorizationUrl = environment.authRoot + this.authPath;
    const state = this.generateRandom();
    const responseType = encodeURIComponent(this.responseType);
    const scope = encodeURIComponent(this.scope);
    let redirectUri = this.state.isElectron ? this.callbackUrlLocal : `${environment.webRoot}`;

    if (redirectPage) {
      redirectUri += redirectPage;
    }

    redirectUri = encodeURI(redirectUri);

    const verifier = this.getVerifier() || this.generateCodeVerifier();
    if (!this.getVerifier()) {
      this.setVerifier(verifier);
    }

    const codeChallenge = await this.generateCodeChallenge(verifier);

    // build the URL
    let url = `${authorizationUrl
    }?response_type=${responseType
    }&client_id=${this.clientId
    }&redirect_uri=${redirectUri
    }&scope=${scope
    }&state=${state
    }&code_challenge=${codeChallenge
    }&code_challenge_method=S256`;

    // set the id token hint for token renewal
    if (idTokenHint) {
      url = `${url}&id_token_hint=${idTokenHint}`;
    }

    // store state and authorize
    this.storeState(state);
    return url;
  }

  /**
   * Processes token from callback URL
   */
  private async processToken(): Promise<AuthenticationToken> {
    // get existing token from cookie
    const existingToken = this.existingToken;
    if (existingToken) {
      return existingToken;
    }
    // check hash
    if (!this.getWindowLocationHashOrQuery()) {
      return null;
    }

    // get and parse hash
    const hash = this.getWindowLocationHashOrQuery().substring(1);
    const authObject = hash.split('&').reduce(this.hashParser, {});

    // check errors and state
    if (authObject.error || !this.validateState(authObject.state)) {
      this.notificationService.showNotification(`Authentication error: ${authObject.error}`, NotificationType.Error, true);
      this.updateWindowLocation();
      return;
    }

    try {
      const tokenObj: any =
        await this.http.get(`${this.apiRoot}${this.tokenExchangeEndpoint}/${authObject.code}/${this.getVerifier()}`).toPromise();
      const token: AuthenticationToken = {
        accessToken: tokenObj.accessToken,
        idToken: tokenObj.identityToken,
        expires: new Date(new Date().getTime() + parseInt(tokenObj.expiresIn, 10) * 1000),
        tokenType: tokenObj.tokenType,
        error: tokenObj.error
      };

      // return the token
      window.localStorage.removeItem(this.stateKey);
      this.updateWindowLocation();
      this.storeToken(JSON.stringify(token));

      return token;
    } catch {
      this.updateWindowLocation();
    }
  }

  /**
   * Updates the window location url
   * This helps with unit tests
   * @param url the url
   */
  updateWindowLocationUrl(url) {
    window.location.href = url;
  }

  /**
   * Updates the window location hash
   * This helps with unit tests
   * @param hash the url
   */
  updateWindowLocationHash(hash) {
    window.location.hash = hash;
  }

  /**
   * Returns the window location hash or query string
   */
  getWindowLocationHashOrQuery() {
    if (this.state.isElectron) {
      return window.location.search;
    }
    return window.location.search;
  }

  /**
   * Hash parser reducer - Parses a URL hash and returns an object
   * @param final - The final object
   * @param item - The url part
   */
  private hashParser(final, item) {
    const parts = item.split('=');
    final[parts[0]] = parts[1];
    return final;
  }

  /**
   * Generates a random string
   */
  private generateRandom() {
    return Date.now().toString() + Math.random().toString(36);
  }

  /**
   * Stores the state string before authentication
   * @param tokenState - The token state
   */
  private storeState(tokenState: string) {
    window.localStorage.setItem(this.stateKey, tokenState);
  }

  /**
   * Validates the token state after authentication
   * @param tokenState - The token state
   */
  private validateState(tokenState: string) {
    return window.localStorage.getItem(this.stateKey) === tokenState;
  }

  /**
   * Retrieves the token from cookie/sessionStorage
   */
  private retrieveToken() {
    if (this.state.isElectron) {
      if (this.authenticatedOffline) {
        const offlineToken = window.localStorage.getItem(this.offlineTokenKey);
        if (offlineToken) {
          return offlineToken;
        }
      }
      return window.sessionStorage.getItem(this.cookieKey);
    }
    return this.cookieService.get(this.cookieKey);
  }

  /**
   * Stores the token in cookie/sessionStorage
   * @param token - The token to be stored
   */
  private storeToken(token: string) {
    if (this.state.isElectron) {
      window.sessionStorage.setItem(this.cookieKey, token);
    } else {
      this.cookieService.set(this.cookieKey, token, undefined, '/');
    }
  }

  /**
   * Loads the Silent Renew page in the iframe with the new token
   * @param url - The url with the new token
   */
  private loadSilentRenewFrame(url: string) {
    document.getElementById('silentRenew').setAttribute('src', url);
  }

  generateCodeVerifier() {
    const randomArr = new Uint32Array(28);
    window.crypto.getRandomValues(randomArr);
    return Array.from(randomArr, dec => ('0' + dec.toString(16)).slice(-2)).join('');
  }

  private async generateCodeChallenge(codeVerifier) {
    const hashed = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(codeVerifier));
    return btoa(String.fromCharCode.apply(null, new Uint8Array(hashed)))
      .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }

  private getVerifier() {
    return window.sessionStorage.getItem('codeVerifier');
  }

  private setVerifier(verifier: string) {
    return window.sessionStorage.setItem('codeVerifier', verifier);
  }

  private updateWindowLocation() {
    if (this.state.isElectron) {
      window.location.href = 'http://localhost/callback/';
    } else {
      window.location.href = window.location.href.split('?')[0];
    }
  }

  /**
   * Initializes API root
   */
  private initializeApiRoot(isElectron: boolean) {
    if (isElectron) {
      this.apiRoot = environment.offlineApiRoot;
    } else {
      this.apiRoot = environment.webRoot;
    }
  }
}
